import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CalendarClock, AlertTriangle, Loader2, Plus, X } from 'lucide-react';
import { useAuth, API } from '../App';
import BottomNavigation from '../components/BottomNavigation';
import useAuthorityProfile from '../hooks/useAuthorityProfile';

/**
 * AuthorityScheduledAlerts — Iter 205a.
 *
 * Lightweight self-serve page for an Official Authority to:
 *   • Review their own queued (future-publish) safety alerts.
 *   • Cancel any of them before the background publisher fires.
 *
 * Reuses `/api/safety-bulletin/mine?status=scheduled` so officials never
 * need to talk to an admin to cancel a typo'd schedule. Renders an empty
 * state with a clear CTA back to the submit form.
 */
const AuthorityScheduledAlerts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const authority = useAuthorityProfile();
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      // No dedicated "mine" endpoint yet — query the admin scheduled list
      // is admin-only. We instead fall back to filtering the public feed
      // is impossible (scheduled excluded from public). So we do a focused
      // round-trip: ask the backend for everything, then filter to ours.
      const r = await axios.get(`${API}/safety-bulletin/mine?status=scheduled`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setBulletins(r.data?.bulletins || []);
    } catch (err) {
      // Endpoint may not exist yet — surface a friendly message instead of failing silently.
      toast.error(err?.response?.data?.detail || 'Could not load scheduled alerts');
      setBulletins([]);
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (b) => {
    if (!window.confirm(`Cancel scheduled publish of "${b.title}"?`)) return;
    setBusyId(b.id);
    try {
      await axios.post(
        `${API}/safety-bulletin/${b.id}/cancel-schedule`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      toast.success('Scheduled alert cancelled');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusyId('');
    }
  };

  // Guard: only authority users land here. Resident -> bounce home.
  useEffect(() => {
    if (!authority.loading && !authority.isOfficial && user) {
      navigate('/safety-bulletin', { replace: true });
    }
  }, [authority.loading, authority.isOfficial, user, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-gradient-to-r from-amber-600 to-amber-700 px-4 py-5 text-white">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1 -ml-1 rounded hover:bg-white/10"
            data-testid="authority-scheduled-back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <CalendarClock className="h-5 w-5" /> My Scheduled Alerts
            </h1>
            <p className="text-xs text-amber-50/90">
              {authority.displayName ? `${authority.displayName} · ${authority.town}` : 'Authority queue'}
            </p>
          </div>
          <Link
            to="/safety-bulletin/submit"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white text-amber-800 text-sm font-semibold hover:bg-amber-50"
            data-testid="authority-scheduled-new-btn"
          >
            <Plus className="h-4 w-4" /> New
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5" data-testid="authority-scheduled-list">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : bulletins.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-center" data-testid="authority-scheduled-empty">
            <CalendarClock className="h-8 w-8 text-amber-600 mx-auto mb-2" />
            <h2 className="text-base font-bold text-slate-900 mb-1">No scheduled alerts yet</h2>
            <p className="text-sm text-slate-500 mb-4">
              When you schedule a Safety Alert for a future date and time, it appears here so you can review or cancel before it goes live.
            </p>
            <Link
              to="/safety-bulletin/submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold"
              data-testid="authority-scheduled-empty-cta"
            >
              <Plus className="h-4 w-4" /> Schedule your first alert
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {bulletins.map((b) => (
              <li
                key={b.id}
                className="bg-white border-2 border-indigo-200 rounded-xl p-4"
                data-testid={`authority-scheduled-item-${b.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800 text-xs font-bold ring-1 ring-indigo-300">
                    <CalendarClock className="h-3 w-3" />
                    {(b.scheduledAt || b.publishedAt || '').slice(0, 16).replace('T', ' ')}
                  </span>
                  {b.priority && b.priority !== 'normal' && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                      b.priority === 'critical' ? 'bg-red-100 text-red-800 ring-1 ring-red-300'
                      : b.priority === 'high' ? 'bg-orange-100 text-orange-800 ring-1 ring-orange-300'
                      : 'bg-slate-100 text-slate-600 ring-1 ring-slate-300'
                    }`}>
                      {b.priority}
                    </span>
                  )}
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-1">{b.title}</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-snug mb-3">{b.body}</p>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{b.town} · {b.category}</span>
                  <button
                    onClick={() => cancel(b)}
                    disabled={busyId === b.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-800 ring-1 ring-rose-200 text-xs font-semibold disabled:opacity-60"
                    data-testid={`authority-scheduled-cancel-${b.id}`}
                  >
                    {busyId === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                    Cancel
                  </button>
                </div>
                {b.category === 'community_alert' && (
                  <p className="mt-2 text-[11px] text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Will notify followers of {b.town} when published.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
};

export default AuthorityScheduledAlerts;
